import { getApiClient } from "@src/api";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { graphql } from "@src/gql/gql";
import type { WhiteboardPermission } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const grantPermissionsQuery = graphql(/* GraphQL */ `
	mutation whiteboardMutations($whiteboardId:String!,$userEmails:[String!]!,$permission:WhiteboardPermission!) {
		whiteboards {
			grantPermissions(whiteboardId:$whiteboardId, userEmails: $userEmails, permission: $permission) {
				name
				ownerId
				status
				permissions {
					publishedLink {
						isReadonly
						key
					}
					userPermissions {
						key
						value
					}
				}
			}
		}
	}
`);

/**
 * Grant permission to users on a whiteboard.
 *
 * @returns React Query mutation object
 */
export const useGrantWhiteboardPermission = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			whiteboardId,
			userEmails,
			permission,
		}: {
			whiteboardId: string;
			userEmails: string[];
			permission: WhiteboardPermission;
		}) => {
			const result = await client.mutate({
				mutation: grantPermissionsQuery,
				variables: {
					whiteboardId,
					userEmails,
					permission,
				},
			});

			return result.data ?? null;
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			void queryClient.invalidateQueries({
				queryKey: libraryQueryKeys.entityTypeCounts.all,
			});
		},
	});
};
